<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import OnboardingAPI from 'dashboard/api/onboarding';
import OptionalStepGate from './OptionalStepGate.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import CSATDisplayTypeSelector from 'dashboard/routes/dashboard/settings/inbox/settingsPage/components/CSATDisplayTypeSelector.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { resolveTargetInbox } from './resolveTargetInbox';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(9)
);

// Backed by the real inbox record (same csat_survey_enabled/csat_config columns
// as Settings > Inbox > Customer Satisfaction) instead of a wizard-only draft, so
// this step stays in sync with that screen in both directions — same pattern as
// Step02Agents/Step03Teams/Step07CannedResponses for their own real resources.
const inboxId = ref(null);
const csatSurveyEnabled = ref(false);
const message = ref(t('ONBOARDING.STEP_9.DEFAULT_MESSAGE'));
const displayType = ref('emoji');
const surveyRules = ref({ operator: 'contains', values: [] });
const saveError = ref(null);

const hasDraft = computed(() => csatSurveyEnabled.value);

const isValid = computed(
  () => csatSurveyEnabled.value && !!message.value.trim()
);
watch(isValid, value => emit('update:can-advance', value), { immediate: true });

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 9])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const applyInboxData = inbox => {
  csatSurveyEnabled.value = inbox.csat_survey_enabled || false;
  const config = inbox.csat_config || {};
  message.value = config.message || t('ONBOARDING.STEP_9.DEFAULT_MESSAGE');
  displayType.value = config.display_type || 'emoji';
  surveyRules.value = config.survey_rules || {
    operator: 'contains',
    values: [],
  };
};

// Enables CSAT for real as soon as the field is reachable — mirrors Step06LeadReturn
// persisting on selection rather than waiting for a separate save button.
const persistCsat = async () => {
  if (!inboxId.value) return;
  try {
    await InboxesAPI.update(inboxId.value, {
      csat_survey_enabled: true,
      csat_config: {
        display_type: displayType.value,
        message: message.value,
        survey_rules: surveyRules.value,
      },
    });
    csatSurveyEnabled.value = true;
    saveError.value = null;
    if (!isStepAlreadyCompleted.value) await markStepCompleted();
  } catch (error) {
    saveError.value =
      error?.response?.data?.error || t('ONBOARDING.STEP_9.UPDATE_ERROR');
  }
};

// "Yes" on the gate question means the account wants CSAT — enable it right away
// (with whatever defaults are already loaded) instead of waiting for the user to
// also focus/blur the message field, which they might skip entirely if they're
// happy with the defaults and go straight to "Próximo".
const enableCsatIfNeeded = () => {
  if (!csatSurveyEnabled.value) persistCsat();
};

const updateDisplayType = type => {
  displayType.value = type;
  persistCsat();
};

// Re-syncs when CSAT is changed from another screen (e.g. Settings > Inbox >
// Customer Satisfaction) so both views stay live.
const refreshFromServer = async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (!inbox) return;

    inboxId.value = inbox.id;
    applyInboxData(inbox);
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await InboxesAPI.get();
    const inbox = resolveTargetInbox(data?.payload || [], props.progress);
    if (inbox) {
      inboxId.value = inbox.id;
      applyInboxData(inbox);
    }
    if (csatSurveyEnabled.value && !isStepAlreadyCompleted.value) {
      await markStepCompleted();
    }
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.INBOX_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_9.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/421-csat-medindo-a-satisfacao-de-quem-foi-atendido"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_9.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_9.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.OPTIONAL_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">{{ t('ONBOARDING.STEP_9.DESCRIPTION') }}</p>

      <OptionalStepGate
        :step="9"
        :progress="progress"
        :has-draft="hasDraft"
        :question="t('ONBOARDING.STEP_9.GATE_QUESTION')"
        :now-label="t('ONBOARDING.STEP_9.GATE_NOW')"
        :now-description="t('ONBOARDING.STEP_9.GATE_NOW_DESCRIPTION')"
        :later-label="t('ONBOARDING.STEP_9.GATE_LATER')"
        :later-description="t('ONBOARDING.STEP_9.GATE_LATER_DESCRIPTION')"
        @refresh="emit('refresh')"
        @update:can-advance="emit('update:can-advance', $event)"
        @choose-yes="enableCsatIfNeeded"
      >
        <div class="flex flex-col w-full max-w-2xl gap-2 mb-4">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('INBOX_MGMT.CSAT.DISPLAY_TYPE.LABEL') }}
          </span>
          <CSATDisplayTypeSelector
            :selected-type="displayType"
            @update="updateDisplayType"
          />
        </div>

        <TextArea
          v-model="message"
          :label="t('INBOX_MGMT.CSAT.MESSAGE.LABEL')"
          :placeholder="t('ONBOARDING.STEP_9.MESSAGE_PLACEHOLDER')"
          :max-length="200"
          show-character-count
          class="w-full max-w-2xl"
          @focus="persistCsat"
          @blur="persistCsat"
        />
        <p v-if="saveError" class="mt-2 text-sm text-n-ruby-9">
          {{ saveError }}
        </p>
      </OptionalStepGate>
    </div>
  </div>
</template>
